import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { ga4Tracker } from '@/utils/ga4Tracking';
import { useInterestDataSync } from '@/hooks/useInterestData';
import { openURL } from '@/utils/navigationUtils';
import { decodeText } from '@/utils/textDecoding';

interface SimilarVehiclesData {
  similar_vehicles: string;
  hasVehicles?: boolean;
  cta_text?: string;
}

interface SimilarVehiclesMessageProps {
  data: SimilarVehiclesData;
}

const SimilarVehiclesMessage: React.FC<SimilarVehiclesMessageProps> = ({ data }) => {
  const { t } = useLanguage();
  const storedInterest = useInterestDataSync();
  
  if (!data) {
    return null;
  }
  
  if (!data.similar_vehicles) {
    return null;
  }
  
  const handleClick = async () => {
    const url = data.similar_vehicles;
    
    // Track GA4 similar vehicles view event
    if (storedInterest?.vehicle) {
      const vehicleData = {
        vehicle_make: storedInterest.vehicle.make,
        vehicle_model: storedInterest.vehicle.model,
        vehicle_year: storedInterest.vehicle.year,
        vehicle_trim: storedInterest.vehicle.version,
        vehicle_color: storedInterest.vehicle.color,
        vehicle_vin: storedInterest.vehicle.vin,
        vehicle_stock: storedInterest.vehicle.stock
      };
      ga4Tracker.trackSimilarVehiclesView(vehicleData);
    }
    
    // Track chat interaction
    ga4Tracker.trackChatInteraction({
      interaction_type: 'similar_vehicles',
      message_type: 'button_click'
    });
    
    await openURL(url);
  };
  
  const getButtonText = () => {
    // If custom CTA text is provided, use it
    if (data.cta_text) {
      return data.cta_text;
    }
    
    const url = data.similar_vehicles.toLowerCase();
    
    // Check for specific URL patterns and return custom text
    if (url.includes('/inventaire/calculatrice-pret-auto.html')) {
      return 'Calculatrice de Prêt Auto';
    }
    if (url.includes('/inventory/car-loan-calculator.html')) {
      return 'Car Loan Calculator';
    }
    if (url.includes('/preferences-de-communication.html')) {
      return 'Préférences de communication';
    }
    if (url.includes('/communication-preferences.html')) {
      return 'Communication Preferences';
    }
    if (url.includes('/termes-et-conditions.html')) {
      return 'Termes et conditions';
    }
    if (url.includes('/terms-and-conditions.html')) {
      return 'Terms and Conditions';
    }
    if (url.includes('politique-de-confidentialite.html')) {
      return 'Politique de Confidentialité';
    }
    if (url.includes('/privacy-policy.html')) {
      return 'Privacy Policy';
    }
    
    // Default to inventory text
    return t('chat.similar.models');
  };
  
  const buttonText = decodeText(getButtonText());
  
  return (
    <div className="mb-2 flex justify-center min-w-0">
      <Button 
        className="!bg-black !text-white hover:!bg-gray-700 hover:!text-white rounded-lg flex items-center gap-2 h-auto max-w-full whitespace-normal text-left"
        onClick={handleClick}
      >
        <span className="min-w-0 break-words">{buttonText}</span>
        <ArrowRight className="w-4 h-4 flex-shrink-0" strokeWidth={3} />
      </Button>

    </div>
  );
};

export default SimilarVehiclesMessage;
